import { useRef } from 'react';
import {
  Button,
  Popover,
  PopoverAnchor,
  PopoverContent,
  type PopoverAnchorProps,
} from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import {
  InfoMenuType,
  type ImageAsset,
  type InfoMenuComponentProps,
  type InfoMenuProps,
} from './InfoMenu.types';
import { useTranslation } from 'react-i18next';

function DateInfo({ value }: InfoMenuComponentProps) {
  const date = new Date(value as string);
  return (
    <span className="wrap-break-word text-body-md text-muted-foreground">
      {date.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
      }) +
        ', ' +
        date.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        })}
    </span>
  );
}

function TextInfo({ value }: InfoMenuComponentProps) {
  return (
    <span className="wrap-break-word text-body-md text-muted-foreground">{value as string}</span>
  );
}

function ImageWithUrl({ assetId, fileName }: { assetId: string; fileName: string }) {
  const { downloadUrl, isSuccess } = useGetDownloadUrl(assetId);
  if (!isSuccess) return null;
  return (
    <img key={assetId} src={downloadUrl} alt={fileName} className="size-10 object-cover rounded" />
  );
}

function ImageInfo<T extends ImageAsset>({ value }: InfoMenuComponentProps<T>) {
  const images = value as T[];

  return (
    <div className="flex gap-1 flex-wrap w-full">
      {images.map((image) => (
        <ImageWithUrl
          key={image.assetId}
          assetId={image.assetId}
          fileName={image.metadata.fileName}
        />
      ))}
    </div>
  );
}

/**
 * Controlled InfoMenu component
 * Parent is responsible for managing open state and anchor element
 */
export function InfoMenu<T extends ImageAsset = ImageAsset>({
  input,
  anchorEl,
  open,
  onClose,
}: InfoMenuProps<T>) {
  const { t } = useTranslation('contentTest');

  /**
   * Callers hand us an already-resolved DOM node rather than rendering a
   * trigger, so the popover is positioned against a virtual anchor instead of
   * one of its own children.
   */
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = anchorEl;

  const componentsMap = {
    [InfoMenuType.Date]: DateInfo,
    [InfoMenuType.Text]: TextInfo,
    [InfoMenuType.LongText]: TextInfo,
    [InfoMenuType.Image]: ImageInfo,
  };

  return (
    <Popover open={open} onOpenChange={(next) => !next && onClose()}>
      <PopoverAnchor virtualRef={anchorRef as PopoverAnchorProps['virtualRef']} />
      <PopoverContent align="end" className="w-85 rounded-2xl border-border p-0 shadow-none">
        <ul className="p-4 space-y-6.5">
          <li key="header" className="flex justify-between items-center w-full">
            <span className="text-body-lg font-medium">{t('widget.infoMenu.promptInfo')}</span>
            <Button
              variant="outline"
              size="icon-mini"
              roundness="full"
              className="size-6"
              onClick={onClose}
              aria-label={t('widget.infoMenu.close')}
            >
              <XIcon className="size-3" />
            </Button>
          </li>
          {input
            ? input.map((part) => {
                return Object.entries(part).map(([key, data]) => {
                  const Component = componentsMap[data.type];
                  return (
                    <li
                      key={key}
                      className="flex justify-between gap-1.5 flex-wrap w-full items-center"
                    >
                      <span className="flex-1 self-start text-body-lg">{key}</span>
                      <Component value={data.value} />
                    </li>
                  );
                });
              })
            : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
